import { entityStore } from '~/utility/BinSentry-ui-utility';
import { BnModalMixin } from '../../common/bn-modal-mixin';
import { html, LitEntity, renderIf, nothing } from '../../utility/lit-entity';
import '../../common/bn-property';
import '../../common/bn-banner';

class MicrosoftFinanceAndOperationsIntegrationModal extends BnModalMixin(LitEntity) {
  constructor() {
    super();
    this.id = 'microsoft-finance-and-operations-integration-modal';
  }

  static get properties() {
    return {
      applyingCredentials: { type: Boolean, attribute: false },
      statusMessage: { type: String, attribute: false },
      statusSeverity: { type: String, attribute: false },
    };
  }

  renderContents() {
    return html`
      <style>
        bn-modal#microsoft-finance-and-operations-integration-modal bn-banner {
          padding: 0 10px;
        }
        bn-modal#microsoft-finance-and-operations-integration-modal .description {
          padding: 0 20px 10px 20px;
        }
        bn-modal#microsoft-finance-and-operations-integration-modal .status {
          font-weight: bold;
        }
        bn-modal#microsoft-finance-and-operations-integration-modal .action-surface {
          position: relative;
        }
        bn-modal#microsoft-finance-and-operations-integration-modal .hidden {
          visibility: hidden;
        }
        bn-modal#microsoft-finance-and-operations-integration-modal .load-spinner {
          position: absolute;
          width: 100%;
        }
      </style>
      <h3 slot="title">${this.title}</h3>
      <div class="description">
        <p>
          The Finance and Operations integration allows forwarding orders from BinSentry to Finance and Operations.
        </p>
        <p>
          Microsoft credentials must be provided for the integration to work.
        </p>
        <p>
          Once the credentials are configured, the following mappings are required in order to forward orders. Any orders
            that are not correctly mapped will not be synced to F&amp;O.
        </p>
        <ul>
          <li>
            Customer organizations will need the tag with the key: financeAndOperations.CustomerAccount
              - The value is the customer account number in F&amp;O to map
          </li>
          <li>
              The formulaId for all <a href="/feeds">feeds</a> must be mapped to a product item number in F&amp;O
          </li>
        </ul>
      </div>
      ${this.statusMessage ? html`<bn-banner severity='${this.statusSeverity}'>${this.statusMessage}</bn-banner` : nothing}

      <bn-surface>
        <div>
          <label>Status:</label>
          <span class="status">${this.entity.properties.credentialsConfigured ? 'Enabled' : 'Disabled'}</span>
        </div>
      </bn-surface>

      ${renderIf(this.entity.hasAction('set-credentials'), () => html`
        <bn-surface class="action-surface">
          ${renderIf(this.applyingCredentials, () => html`<div class="load-spinner"><bn-loading><p>Saving credentials...</p></bn-loading></div>`)}
          <bn-action
            class="${this.applyingCredentials ? 'hidden' : ''}"
            .href="${this.href}"
            actionname="set-credentials"
            @submit="${this._onCredentialsSubmit}"
            @response="${this._onCredentialsResponse}"
            @error="${this._onCredentialsError}"
            suppressflash
          >
            <h3 slot="header">
              ${this.entity.properties.credentialsConfigured ? 'Update' : 'Set'} credentials
            </h3>
          </bn-action>
        </bn-surface>
      `)}

      ${renderIf(this.entity.hasAction('delete-credentials'), () => html`
        <bn-surface>
          <bn-action
            .href="${this.href}"
            actionname="delete-credentials"
            submittext="Delete credentials"
            buttonstyle='outlinedError'
            .preSubmit="${this._confirmDeletingInstallerCredentials}"
            @response="${() => this._onResponse('Microsoft Finance and Operations integration has been disabled.')}"
            @error="${(e) => this._onError(e, 'Could not delete Microsoft Finance and Operations credentials.')}"
          >
            <h3 slot="header">Disable Microsoft Finance and Operations integration</h3>
          </bn-action>
        </bn-surface>
      `)}
    `;
  }

  async _onCredentialsSubmit() {
    this.applyingCredentials = true;
  }

  async _onCredentialsResponse() {
    this.applyingCredentials = false;
    return this._onResponse('Credentials have been configured. Microsoft Finance and Operations integration is enabled.');
  }

  async _onCredentialsError(e) {
    this.applyingCredentials = false;
    return this._onError(e, 'Could not set Microsoft Finance and Operations credentials.');
  }

  async _onResponse(successText) {
    this.statusMessage = null;
    this.statusSeverity = null;
    await entityStore.fetchWait(this.getHref('self'));
    this.statusMessage = successText;
    this.statusSeverity = 'success';
  }

  async _onError(event, errorText) {
    this.statusMessage = errorText;
    this.statusSeverity = 'critical';
  }

  _confirmDeletingInstallerCredentials() {
    const confirmMessage = 'Are you sure you want to delete Finance and Operations credentials? This will disable Finance and Operations integration.';
    return window.confirm(confirmMessage);
  }
}

window.customElements.define('microsoft-finance-and-operations-integration-modal', MicrosoftFinanceAndOperationsIntegrationModal);
